import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@bcc/components/ui";
import { useTranslation } from "react-i18next";
import type { SetupGuidePage } from "../tour.types";
import { setTourTestId } from "../tour.utils";

export interface TourFinishCopyKeys {
  readonly titleKey: string;
  /** Shown after completing the last step */
  readonly descriptionKey: string;
  /** Shown alone after a skip, under the description after completion */
  readonly relaunchHintKey: string;
  readonly doneKey: string;
}

export const TOUR_FINISH_COPY_KEYS: TourFinishCopyKeys = {
  titleKey: "tour.finish.title",
  descriptionKey: "tour.finish.description",
  relaunchHintKey: "tour.finish.relaunchHint",
  doneKey: "tour.finish.done",
};

/** Finish copy for the onboarding setup guide; the description differs per page */
export const buildSetupGuideFinishCopyKeys = (page: SetupGuidePage): TourFinishCopyKeys => ({
  titleKey: "tour.setupGuide.finish.title",
  descriptionKey: `tour.setupGuide.finish.description.${page}`,
  relaunchHintKey: "tour.setupGuide.finish.relaunchHint",
  doneKey: "tour.finish.done",
});

/** Finish copy for the "what's new" tour */
export const WHATS_NEW_FINISH_COPY_KEYS: TourFinishCopyKeys = {
  titleKey: "tour.whatsNew.finish.title",
  descriptionKey: "tour.whatsNew.finish.description",
  relaunchHintKey: "tour.whatsNew.finish.relaunchHint",
  doneKey: "tour.finish.done",
};

export interface TourFinishDialogProps {
  readonly isOpen: boolean;
  /** True when the tour was skipped — shows only the relaunch hint, not the end-of-tour recap */
  readonly wasSkipped?: boolean;
  /** Closes the dialog and marks the tour completed */
  readonly onDone: () => void;
  /** Defaults to the full tour's finish copy */
  readonly copyKeys?: TourFinishCopyKeys;
}

export function TourFinishDialog({
  isOpen,
  wasSkipped = false,
  onDone,
  copyKeys = TOUR_FINISH_COPY_KEYS,
}: Readonly<TourFinishDialogProps>) {
  const { t } = useTranslation();
  return (
    <Dialog
      open={isOpen}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onDone();
      }}
    >
      <DialogContent className="z-[70]" testId={setTourTestId("finish-dialog")}>
        <DialogHeader>
          <DialogTitle testId={setTourTestId("finish-title")}>{t(copyKeys.titleKey) as string}</DialogTitle>
          <DialogDescription testId={setTourTestId("finish-description")}>
            {t(wasSkipped ? copyKeys.relaunchHintKey : copyKeys.descriptionKey) as string}
          </DialogDescription>
        </DialogHeader>
        {!wasSkipped && (
          <p className="text-muted-foreground text-sm" data-testid={setTourTestId("finish-relaunch-hint")}>
            {t(copyKeys.relaunchHintKey) as string}
          </p>
        )}
        <DialogFooter>
          <Button onClick={onDone} testId={setTourTestId("finish-done")}>
            {t(copyKeys.doneKey) as string}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
